import React, { memo, useState, useEffect, useCallback } from 'react';
import { getThresholdAlert, patchThresholdAlert } from '../../utils/services/userAPI';
import Loader from '../../components/Loader';
import { LineSeparator, StyledAlertsAndNotifications, UserDetailHeadingCover } from './styles';
import { Icon, Input, InputNumber, Popconfirm, Select, Tooltip, message } from 'antd';
import REGEX from '../../utils/regex';
import _ from 'underscore';
import { ALERT_AND_NOTIFICATIONS as trans } from './TranslationValues';
import { withTranslation } from 'react-i18next';

const AlertsAndNotifications = ({ t }) => {
  const [emailIds, setEmailIds] = useState([]);
  const [threshold, setThreshold] = useState(90);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [addingEmailId, setAddingEmailId] = useState(false);
  const [deletingEmailId, setDeletingEmailId] = useState({});
  const [updatingThreshold, setUpdatingThreshold] = useState(false);

  useEffect(() => {
    fetchThresholdAlert();
  }, []);

  const fetchThresholdAlert = useCallback(async () => {
    try {
      const response = await getThresholdAlert();
      if (response?.statusCode === 3117 || response?.statusCode === 3118) {
        const ids = !!response.data?.emailIds ? response.data?.emailIds.split(',') : [];
        setEmailIds(ids);
        response.data?.threshold && setThreshold(response.data?.threshold);
      } else {
        message.error(
          t(trans.GET_THRESHOLD_ERROR_MESSAGE.key, trans.GET_THRESHOLD_ERROR_MESSAGE.value),
        );
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addEmailId = useCallback(
    async e => {
      const emailId = e.target.value;
      if (!REGEX.EMAIL.test(emailId)) {
        message.error(
          t(
            trans.INVALID_EMAIL_ADDRESS_ERROR_MESSAGE.key,
            trans.INVALID_EMAIL_ADDRESS_ERROR_MESSAGE.value,
          ),
        );
        return;
      }
      if (emailIds.includes(emailId)) {
        message.error(
          t(trans.EMAIL_ID_EXISTS_ERROR_MESSAGE.key, trans.EMAIL_ID_EXISTS_ERROR_MESSAGE.value),
        );
        return;
      }
      const mergedEmailIds = [...emailIds, emailId];
      setAddingEmailId(true);
      try {
        const payload = {
          emailIds: mergedEmailIds.toString(),
          threshold,
        };
        const response = await patchThresholdAlert(payload);
        if (response?.statusCode === 3114) {
          setInputValue('');
          setEmailIds(mergedEmailIds);
        } else {
          message.error(
            t(
              trans.ADD_EMAIL_ADDRESS_ERROR_MESSAGE.key,
              trans.ADD_EMAIL_ADDRESS_ERROR_MESSAGE.value,
            ),
          );
        }
      } catch (e) {
        console.error(e);
      } finally {
        setAddingEmailId(false);
      }
    },
    [emailIds, threshold],
  );

  const deleteEmailId = useCallback(
    async emailId => {
      setDeletingEmailId(prevState => ({ ...prevState, [emailId]: true }));

      const updatedIds = emailIds.filter(id => id !== emailId);
      try {
        const payload = {
          emailIds: updatedIds.toString(),
          threshold,
        };
        const response = await patchThresholdAlert(payload);
        if (response?.statusCode === 3114) {
          setEmailIds(updatedIds);
        } else {
          message.error(
            t(
              trans.DELETE_EMAIL_ADDRESS_ERROR_MESSAGE.key,
              trans.DELETE_EMAIL_ADDRESS_ERROR_MESSAGE.value,
            ),
          );
        }
      } catch (e) {
        console.error(e);
      } finally {
        setDeletingEmailId(prevState => ({ ...prevState, [emailId]: false }));
      }
    },
    [emailIds, threshold],
  );

  const updateThreshold = useCallback(
    _.debounce(async threshold => {
      if (!Number(threshold) || threshold <= 0 || threshold > 100) {
        message.error(
          t(trans.INVALID_THRESHOLD_ERROR_MESSAGE.key, trans.INVALID_THRESHOLD_ERROR_MESSAGE.value),
        );
        return;
      }
      setUpdatingThreshold(true);
      try {
        const payload = {
          emailIds: emailIds.toString(),
          threshold,
        };
        const response = await patchThresholdAlert(payload);
        if (response?.statusCode !== 3114) {
          message.error(
            t(trans.UPDATE_THRESHOLD_ERROR_MESSAGE.key, trans.UPDATE_THRESHOLD_ERROR_MESSAGE.value),
          );
        }
      } catch (e) {
        console.error(e);
      } finally {
        setUpdatingThreshold(false);
      }
    }, 500),
    [emailIds],
  );

  const handleThresholdChange = useCallback(
    value => {
      setThreshold(value);
      updateThreshold(value);
    },
    [updateThreshold],
  );

  return isLoading ? (
    <Loader />
  ) : (
    <StyledAlertsAndNotifications>
      <UserDetailHeadingCover separator>
        <div>
          <h5>
            {t(
              trans.ALERTS_AND_NOTIFICATIONS_HEADING.key,
              trans.ALERTS_AND_NOTIFICATIONS_HEADING.value,
            )}
          </h5>
          <p>
            {t(
              trans.ALERTS_AND_NOTIFICATIONS_DESCRIPTION.key,
              trans.ALERTS_AND_NOTIFICATIONS_DESCRIPTION.value,
            )}
          </p>
        </div>
      </UserDetailHeadingCover>
      <LineSeparator />
      {emailIds.length > 0 && (
        <>
          <p className="sub-heading-text">
            {t(trans.ALERT_RECIPIENTS_LABEL.key, trans.ALERT_RECIPIENTS_LABEL.value)}
          </p>
          <Select
            mode="tags"
            size="large"
            value={emailIds}
            open={false}
            className="availableLanguages"
          >
            {emailIds.map(id => (
              <Select.Option key={id}>
                {id}
                {deletingEmailId[id] ? (
                  <Icon type={'loading'} className="delete-loading-icon" />
                ) : (
                  <Popconfirm
                    title={t(
                      trans.DELETE_CONFIRMATION_TITLE.key,
                      trans.DELETE_CONFIRMATION_TITLE.value,
                    )}
                    okText={t(
                      trans.DELETE_POPOVER_YES_TEXT.key,
                      trans.DELETE_POPOVER_YES_TEXT.value,
                    )}
                    cancelText={t(
                      trans.DELETE_POPOVER_NO_TEXT.key,
                      trans.DELETE_POPOVER_NO_TEXT.value,
                    )}
                    onConfirm={() => deleteEmailId(id)}
                  >
                    <Icon type={'delete'} />
                  </Popconfirm>
                )}
              </Select.Option>
            ))}
          </Select>
        </>
      )}
      <div className="input-container">
        <div>
          <p className="sub-heading-text">
            {t(trans.ADD_EMAIL_ADDRESS_LABEL.key, trans.ADD_EMAIL_ADDRESS_LABEL.value)}
          </p>
          <Input.Search
            size={'large'}
            loading={addingEmailId}
            placeholder={t(
              trans.EMAIL_ADDRESS_PLACEHOLDER.key,
              trans.EMAIL_ADDRESS_PLACEHOLDER.value,
            )}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onPressEnter={addEmailId}
          />
        </div>
        <div>
          <p className="sub-heading-text">
            {t(trans.THRESHOLD_LABEL.key, trans.THRESHOLD_LABEL.value)}
            {'  '}
            <Tooltip
              placement="top"
              title={t(trans.THRESHOLD_INFO.key, trans.THRESHOLD_INFO.value)}
            >
              <i className="fa-regular fa-circle-question label-help-icon"></i>
            </Tooltip>
          </p>
          <div className="threshold-input">
            <InputNumber
              size={'large'}
              min={0}
              max={100}
              value={threshold}
              disabled={emailIds.length === 0}
              onChange={handleThresholdChange}
              formatter={value => `${value}%`}
            />
            {updatingThreshold && (
              <div className="threshold-saving">
                <Icon type="loading" /> {t(trans.SAVING_TEXT.key, trans.SAVING_TEXT.value)}
              </div>
            )}
          </div>
        </div>
      </div>
    </StyledAlertsAndNotifications>
  );
};

const NAMESPACE = ['ALERT_AND_NOTIFICATIONS'];
export default memo(withTranslation(NAMESPACE)(AlertsAndNotifications));
